import React from "react";

import dayjs from "dayjs";
import Button from "react-bootstrap/Button";
import { Link, useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import Table from "../../../../components/Table";
import { QUERY_CHAT_MESSAGE_TEMPLATES } from "../../../../config/graphql/query";
import { useClientId } from "../hooks/useClient";

// Twice Bootstrap's badge padding (.25em .4em) — the default reads as cramped in a table row.
const BADGE_STYLE = { padding: ".5em .8em" };

export const editedByName = (template: IChatMessageTemplate) => {
  const user = template.editedBy;

  return (
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    ""
  );
};

/** Every template, written or not — the server always returns all of them. */
const TemplateList = () => {
  const { t } = useTranslation(["chatLanguages"]);
  const { url } = useRouteMatch();

  const clientId = useClientId();

  const { data } = useQuery<{ chatMessageTemplates: IChatMessageTemplate[] }>(
    QUERY_CHAT_MESSAGE_TEMPLATES,
    {
      skip: !clientId,
      context: { headers: { ClientId: clientId } },
      fetchPolicy: "network-only",
    },
  );

  return (
    <div className="mb-4">
      <h5>{t("chatLanguages:templates.title")}</h5>
      <p className="text-primary mb-2">
        {t("chatLanguages:templates.subtitle")}
      </p>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">{t("chatLanguages:templates.th.type")}</th>
            <th scope="col">{t("chatLanguages:templates.th.status")}</th>
            <th scope="col">{t("chatLanguages:templates.th.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data?.chatMessageTemplates.map((template) => (
            <tr key={template.template}>
              <td>{t(`chatLanguages:templates.type.${template.template}`)}</td>
              <td>
                {template.custom ? (
                  <>
                    <span
                      className="badge badge-primary mr-2"
                      style={BADGE_STYLE}
                    >
                      {t("chatLanguages:templates.status.custom")}
                    </span>
                    <small className="text-muted">
                      {[
                        editedByName(template),
                        template.updatedAt
                          ? dayjs(template.updatedAt).format("DD.MM.YYYY HH:mm")
                          : "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </small>
                  </>
                ) : (
                  <span className="badge badge-secondary" style={BADGE_STYLE}>
                    {t("chatLanguages:templates.status.default")}
                  </span>
                )}
              </td>
              <td>
                <Link to={`${url}/${template.template}`}>
                  <Button size="sm" variant="primary">
                    {t("chatLanguages:templates.button.edit")}
                  </Button>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default TemplateList;
